/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  getCurrentUser,
  getMyAccessRequest,
  getWorkspaces,
  submitAccessRequest,
} from './access.api';
import {
  roleAtLeast,
  type AccessRequest,
  type AccessRequestSubmission,
  type AppRole,
  type CurrentUser,
  type WorkspaceDefinition,
  type WorkspaceId,
} from './access.models';

/** Shared cache key so the gate, the nav and the admin panel all read one resolution. */
export const ACCESS_QUERY_KEY = ['access', 'me'] as const;

/**
 * Not retried: the interesting failure is a 401 from an expired token, and retrying that just
 * delays the redirect to sign-in. Kept fresh for a minute so navigating around the app does not
 * re-ask on every route change, but a grant made moments ago still lands on a reload.
 */
const QUERY_OPTS = { retry: false, staleTime: 60_000 } as const;

/** The signed-in caller's access. `undefined` while it is still resolving. */
export function useCurrentUser(): UseQueryResult<CurrentUser> {
  return useQuery({ queryKey: ACCESS_QUERY_KEY, queryFn: getCurrentUser, ...QUERY_OPTS });
}

/** Workspace catalogue. Rarely changes, so it outlives a single page. */
export function useWorkspaceCatalogue(): UseQueryResult<WorkspaceDefinition[]> {
  return useQuery({
    queryKey: ['access', 'workspaces'],
    queryFn: getWorkspaces,
    retry: false,
    staleTime: 10 * 60_000,
  });
}

/**
 * Whether the caller may reach a workspace.
 *
 * <p>False while the resolution is in flight, so a gated destination is never briefly shown to
 * someone who turns out not to have it. The cost is that gated nav appears a beat after the rest,
 * which is the right way round.
 */
export function useHasWorkspace(workspace: WorkspaceId): boolean {
  const { data } = useCurrentUser();
  return data?.workspaces.includes(workspace) ?? false;
}

/** Whether the caller holds at least a privilege tier. False while resolving, for the same reason. */
export function useHasRole(floor: AppRole): boolean {
  const { data } = useCurrentUser();
  return roleAtLeast(data?.role, floor);
}

/** Cache key for the caller's own request, shared by the form and the waiting screen. */
export const MY_REQUEST_QUERY_KEY = ['access', 'requests', 'mine'] as const;

/**
 * The caller's most recent access request, or null if they have never made one.
 *
 * <p>Only asked when there is a reason to: someone already provisioned has no request screen to
 * show, and firing this on every page load would put a call on the critical path of every
 * session for the benefit of the few who need it.
 */
export function useMyAccessRequest(enabled: boolean): UseQueryResult<AccessRequest | null> {
  return useQuery({
    queryKey: MY_REQUEST_QUERY_KEY,
    queryFn: getMyAccessRequest,
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * Submits a request and seeds the result straight into the cache, so the waiting screen appears
 * from the response rather than after a second round trip that could still be in flight when the
 * form disappears.
 */
export function useSubmitAccessRequest(): UseMutationResult<
  AccessRequest,
  Error,
  AccessRequestSubmission
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitAccessRequest,
    onSuccess: (created) => queryClient.setQueryData(MY_REQUEST_QUERY_KEY, created),
  });
}
